import { ChangeDetectionStrategy, Component, computed, inject, signal, OnInit } from '@angular/core';
import { FormlyModule, type FormlyFieldConfig } from '@ngx-formly/core';
import { AbstractControl, ReactiveFormsModule, ValidationErrors } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { InfoTooltipComponent } from '../info-tooltip.component';
import { FormlyErrorComponent } from '../formly-error.component';
import { LinkButtonComponent } from '../link-button.component';
import { catchError, of, Subject, takeUntil } from 'rxjs';
import { injectDestroy } from 'ngxtension/inject-destroy';
import { ApiMappingsStore } from '../../../features/admin/modules/features/api-mappings/api-mapping.store';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { FuseAlertComponent } from '../../../../@fuse/components/alert';
import { resolveRdwFieldMeta, rdwSubKey, RdwFieldType, type RdwCategoryProfile } from './rdw.types';
import { RDW_CATEGORY_API_FIELDS, RDW_CATEGORY_PROFILE_KEY, resolveRdwCategoryProfile } from './rdw-category.util';
import { ModulesStore } from '../../../features/admin/modules/modules.store';
import { CascadingAutoFillService, type AutoFillResult } from '../../services/cascading-auto-fill.service';
import { UtilityService } from '../../services/utility.service';
import { extractArrayIndicesFromField, setScopedKeyPath } from '../../utils/mapping-condition-evaluator.util';
import { debugLog } from '../../utils/debug-logger';
import { FormHelper } from '../../../features/admin/modules/features/form-builder/form.util';
import type { AutoFillMapping, Entry } from '../../../features/admin/modules/modules.model';
import { ClearFieldButtonComponent } from './clear-field-button.component';
import { buildClearAriaLabel, ClearableFieldType } from './clearable-field.type';

/** One configured vehicle field, with its current value pulled from the object model. */
interface RdwVehicleItem {
  /** RDW apiField this item was configured from (e.g. `merk`). */
  apiField: string;
  /** Local key inside the RDW object value (keyPath-safe form of the apiField). */
  subKey: string;
  /** Mapping-output keyPath the lookup result writes to — absent when the field is not mapped. */
  entryField?: string;
  label: string;
  type: RdwFieldType;
  value: unknown;
}

@Component({
  selector: 'formly-field-rdw',
  template: `
    <div class="flex w-full flex-col">
      <!--
        No field.className here: formly already applies it to the host <formly-field>, so
        repeating it compounded width classes (a half-width field at 50% of 50%).
      -->
      <div class="pb-4">
        @if (props.label) {
          <label class="mb-2 block font-medium" [for]="id">
            {{ props.label }}
            @if (props.required) {
              <span>*</span>
            }
            <app-info-tooltip [tooltip]="props.info"></app-info-tooltip>
          </label>
        }
        <div class="flex items-center gap-3">
          <div
            class="dd-plate dd-clear-field"
            [class.dd-plate--disabled]="isDisabled()">
            <span class="dd-plate-band">NL</span>
            <input
              #plateInput
              class="dd-plate-input"
              type="text"
              autocomplete="off"
              [id]="id"
              [required]="props.required"
              [placeholder]="props.placeholder ? props.placeholder : 'AB123C'"
              [value]="plate()"
              [disabled]="isDisabled()"
              [readOnly]="isReadOnly()"
              (input)="setPlate($any($event.target).value)"
              (keydown.enter)="$event.preventDefault(); lookup()" />
            <app-clear-field-button
              [field]="field"
              [hasValue]="hasClearableValue()"
              [disabled]="isDisabled()"
              [readOnly]="isReadOnly()"
              [ariaLabel]="clearAriaLabel()"
              [focusTarget]="plateInput"
              (clearSelection)="clear()" />
          </div>
          <button
            type="button"
            mat-flat-button
            class="dd-plate-lookup dd-locked-action shrink-0"
            [disabled]="isLoading() || !plate() || isDisabled() || isReadOnly()"
            (click)="lookup()">
            @if (isLoading()) {
              <mat-spinner diameter="18"></mat-spinner>
            } @else {
              Opzoeken
            }
          </button>
        </div>
        @if (invalidPlate()) {
          <div class="mt-1 text-sm text-warn">Geen geldig Nederlands kenteken.</div>
        }
        @if (props.description) {
          <div class="mt-1 text-[15px] text-accent">{{ props.description }}</div>
        }
      </div>

      @if (vehicleItems().length) {
        @if (manual()) {
          <!-- Manual entry: the configured vehicle fields become editable, prefilled with
               whatever a licence-plate lookup already returned (same object keys). Wrapped in a
               bordered card with a heading, matching the design. -->
          <div class="mb-4 rounded-lg border border-[color:var(--fuse-border)] p-4">
            <p class="mb-3 text-sm text-accent">Vul de voertuiggegevens hieronder handmatig in.</p>
            <div class="grid w-full grid-cols-1 gap-3 sm:grid-cols-2">
              @for (item of vehicleItems(); track item.subKey) {
                <mat-form-field class="w-full">
                  <mat-label>{{ item.label }}</mat-label>
                  <input
                    #manualInput
                    matInput
                    [type]="item.type"
                    [value]="item.value ?? ''"
                    [disabled]="isDisabled()"
                    [readOnly]="isReadOnly()"
                    (input)="setField(item.subKey, $any($event.target).value, item.type)" />
                  <span class="dd-clear-suffix" matSuffix>
                    <app-clear-field-button
                      [field]="field"
                      [hasValue]="hasManualValue(item.value)"
                      [disabled]="isDisabled()"
                      [readOnly]="isReadOnly()"
                      [ariaLabel]="manualFieldClearAriaLabel(item.label)"
                      [focusTarget]="manualInput"
                      (clearSelection)="clearField(item.subKey, item.type)" />
                  </span>
                </mat-form-field>
              }
            </div>
          </div>
        } @else if (readOnlyItems().length) {
          <!-- Looked-up (non-manual): show the filled fields read-only. Figma: navy "surface/custom"
               info card; per row a 160px secondary label + primary value, Inter 14/500/140%. -->
          <div class="mb-4 flex flex-col gap-0.5">
            <div class="flex flex-col rounded-lg bg-[#f6faff] p-4 dark:bg-[#030833]">
              @for (item of readOnlyItems(); track item.subKey) {
                <div class="flex items-center gap-2 py-1 text-sm font-medium leading-[1.4]">
                  <!-- Figma dark: label = foreground/secondary #C7C7CC, value = white. text-accent
                       renders too dark on the custom navy card, so pin the dark colors. -->
                  <span class="w-40 flex-none text-accent dark:text-[#C7C7CC]">{{ item.label }}</span>
                  <span class="dark:text-white">{{ item.display }}</span>
                </div>
              }
            </div>
            <div class="flex items-center gap-1 py-1 text-xs font-medium text-accent dark:text-[#C7C7CC]">
              <span class="dd-rdw-dot"></span>
              Gegevens opgehaald via RDW
            </div>
          </div>
        }

        <div class="mb-4">
          <button
            type="button"
            dd-link-button
            class="dd-locked-action"
            [disabled]="isDisabled() || isReadOnly()"
            (click)="toggleManual()">
            {{ manual() ? 'Handmatige invoer verbergen' : 'Voertuig handmatig invoeren' }}
          </button>
        </div>
      }

      @if (lookupError(); as error) {
        <fuse-alert class="pb-6" type="error">{{ error }}</fuse-alert>
      }
      @if (props.required && formControl.touched && formControl.hasError('rdwRequired')) {
        <div class="mt-1 text-sm text-warn">
          {{
            manual() ? 'Voer een geldig kenteken in.' : 'Voer een geldig kenteken in en haal de voertuiggegevens op.'
          }}
        </div>
      }
      <app-formly-error [field]="field"></app-formly-error>
    </div>
  `,
  imports: [
    ReactiveFormsModule,
    FormlyModule,
    MatInputModule,
    MatButtonModule,
    InfoTooltipComponent,
    FormlyErrorComponent,
    LinkButtonComponent,
    MatProgressSpinner,
    FuseAlertComponent,
    ClearFieldButtonComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [
    `
      /* Dutch licence plate keeps its official yellow/blue colors in both light and dark mode. */
      .dd-plate {
        position: relative;
        display: flex;
        align-items: stretch;
        height: 36px;
        flex: 1 1 auto;
        min-width: 0;
        overflow: hidden;
        border-radius: 8px;
        background: #ffe066;
      }

      /* Figma dark: the plate has no visible border (a light border reads as a yellow halo). */
      :host-context(.dark) .dd-plate {
        border-color: transparent;
      }

      .dd-plate-band {
        display: flex;
        align-items: center;
        justify-content: center;
        flex: none;
        padding: 0 12px 0 16px;
        background: #106bd3;
        color: #fff;
        font-size: 14px;
        font-weight: 600;
      }

      .dd-plate-input {
        flex: 1 1 auto;
        min-width: 0;
        border: 0;
        outline: none;
        background: transparent;
        padding: 0 2.25rem 0 0.875rem;
        font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
        font-size: 14px;
        font-weight: 600;
        letter-spacing: 0.05em;
        text-transform: uppercase;
        color: #000;
      }

      .dd-plate-input::placeholder {
        color: rgba(0, 0, 0, 0.4);
        text-transform: none;
      }

      .dd-plate--disabled,
      .dd-plate--disabled .dd-plate-band,
      .dd-plate--disabled .dd-plate-input {
        cursor: not-allowed;
      }

      :host .dd-locked-action:disabled {
        cursor: not-allowed !important;
      }

      .dd-plate app-clear-field-button {
        position: absolute;
        right: 0.5rem;
        top: 50%;
        transform: translateY(-50%);
      }

      /* Secondary filled lookup button (Figma: gray fill, no border, same height as the plate). */
      /* !important: _buttons.scss pins unelevated buttons to 3px !important globally. */
      :host ::ng-deep .dd-plate-lookup.mat-mdc-unelevated-button.mdc-button {
        --mdc-filled-button-container-height: 36px;
        --mdc-filled-button-container-shape: 8px;
        height: 36px;
        min-height: 36px;
        border-radius: 8px !important;
        background-color: #eeeff1;
        color: #000;
      }

      :host-context(.dark) ::ng-deep .dd-plate-lookup.mat-mdc-unelevated-button.mdc-button {
        background-color: rgba(255, 255, 255, 0.12);
        color: #fff;
      }

      .dd-rdw-dot {
        display: inline-block;
        width: 8px;
        height: 8px;
        flex: none;
        border-radius: 9999px;
        background: #106bd3;
      }
    `,
  ],
})
export class FormlyRDWType extends ClearableFieldType implements OnInit {
  destroy$ = injectDestroy();
  apiMappingsStore = inject(ApiMappingsStore);
  modulesStore = inject(ModulesStore);
  cascadingAutoFillService = inject(CascadingAutoFillService);
  isDutchLicensePlate = signal(false);
  isLoading = signal(false);
  invalidPlate = signal(false);
  /** Message shown when the RDW lookup request itself fails (e.g. a 500 from the mapping API). */
  lookupError = signal<string | null>(null);

  /**
   * Emits when a new lookup starts, cancelling whatever the previous one still had running.
   *
   * The sibling fill outlives its lookup: `isLoading` is already off when its query goes out, so a
   * second plate can be looked up while the first is still resolving a category. Without this the
   * slower of the two wins and writes the wrong vehicle's category.
   */
  readonly #lookupRestarted$ = new Subject<void>();

  /** The RDW field's value is an object: `{ plate, manual, <vehicle sub-fields> }`. */
  obj = signal<Record<string, unknown>>({});

  plate = computed(() => (this.obj()['plate'] as string) ?? '');
  manual = computed(() => this.obj()['manual'] === true);

  /** Definitions of the configured vehicle fields (RDW apiFields + their mapping target). */
  /** The API mapping configured on this field, or undefined when it is not (yet) loaded. */
  private activeMapping() {
    return this.apiMappingsStore
      .apiMappings()
      [this.options.formState.entity.id]?.find(m => m.id === this.props['apiMappingId']);
  }

  vehicleFieldDefs = computed<Array<Omit<RdwVehicleItem, 'value'>>>(() => {
    const apiFields =
      (this.props['vehicleFields'] as string[] | undefined) ??
      (this.props['displayFields'] as string[] | undefined) ??
      [];
    if (!apiFields.length) {
      return [];
    }

    const mapping = this.activeMapping();

    return apiFields.map(apiField => {
      const meta = resolveRdwFieldMeta(apiField);
      const entryField = mapping?.outputFieldMappings?.find(o => o.apiField === apiField)?.entryField ?? undefined;
      return { apiField, subKey: rdwSubKey(apiField), entryField, label: meta.label, type: meta.type };
    });
  });

  /** The configured fields with their current values (recomputes as the object model changes). */
  vehicleItems = computed<RdwVehicleItem[]>(() => {
    const model = this.obj();
    return this.vehicleFieldDefs().map(def => ({ ...def, value: model[def.subKey] }));
  });

  /** Filled fields only — used for the read-only (looked-up) view, with dates shown as dd-mm-yyyy. */
  readOnlyItems = computed<Array<RdwVehicleItem & { display: unknown }>>(() =>
    this.vehicleItems()
      .filter(item => item.value !== null && item.value !== undefined && item.value !== '')
      .map(item => ({ ...item, display: item.type === 'date' ? this.formatDisplayDate(item.value) : item.value }))
  );

  /**
   * Required validity: a valid Dutch plate must be present, and either the vehicle was looked up
   * (RDW returned data — we do not check individual fields, since RDW omits some) or manual entry
   * is on (the vehicle fields themselves stay optional for now).
   */
  rdwRequiredValidator = (control: AbstractControl): ValidationErrors | null => {
    if (!this.props.required) {
      return null;
    }
    const value = (control.value ?? {}) as Record<string, unknown>;
    if (!this.validateDutchLicensePlate((value['plate'] as string) ?? '')) {
      return { rdwRequired: true };
    }
    if (value['manual'] === true) {
      return null;
    }
    return value['lookedUp'] === true ? null : { rdwRequired: true };
  };

  ngOnInit() {
    const initial = this.formControl.value;
    this.obj.set(this.normalize(initial));

    // Legacy entries stored the plate as a bare string. The mapping input reads
    // `<keyPath>.plate`, so a string value makes the lookup fail server-side. Write the object
    // shape back instead of migrating the data — with emitEvent, because the lookup posts the
    // form model (`formState.entry`), which only picks up the new value when the change emits.
    if (typeof initial === 'string' && initial !== '') {
      this.formControl.setValue(this.obj());
    }

    this.formControl.addValidators(this.rdwRequiredValidator);
    this.formControl.updateValueAndValidity({ emitEvent: false });

    // Keep the local object mirror in sync with external patches (defaults, resets, etc.).
    // The lookup itself is on demand via the "Opzoeken" button.
    this.formControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => this.obj.set(this.normalize(value)));
  }

  lookup(): void {
    if (this.isLoading() || !this.plate()) {
      return;
    }
    this.runLookup(this.plate());
  }

  /**
   * Deviates: the composite holds more than the plate, and the button's accessible name only
   * promises to clear the plate. Lookup output goes with it (it derives from the cleared plate),
   * but manual mode and manually typed vehicle data survive — yanking the user out of manual
   * entry because they corrected the plate lost real input. Entry fields that a previous lookup
   * filled through the cascading auto-fill mapping are deliberately NOT reverted here: the user
   * may have edited them since, and the mapping writes to controls this field does not own.
   */
  override clear(): void {
    this.#lookupRestarted$.next();
    const current = this.obj();
    let cleared: Record<string, unknown>;
    if (current['manual'] === true) {
      cleared = { ...current, plate: '', lookedUp: false };
      // The category profile is lookup output (written in runLookup, invisible in the manual
      // UI) and feeds the copyFieldsOnSelect query mapping — keeping it would let the cascade
      // keep matching the OLD vehicle's profile after its plate is gone.
      delete cleared[RDW_CATEGORY_PROFILE_KEY];
    } else {
      cleared = { plate: '', manual: false, lookedUp: false };
    }
    this.obj.set(cleared);
    this.formControl.setValue(cleared);
    this.isDutchLicensePlate.set(false);
    this.invalidPlate.set(false);
    this.lookupError.set(null);
    this.isLoading.set(false);
    this.formControl.markAsDirty();
    this.formControl.markAsTouched();
  }

  setPlate(value: string): void {
    // Accept plates typed with dashes/spaces but store the bare form — the RDW API expects the
    // plate without separators. Editing also invalidates any previous lookup.
    this.patch({ plate: (value ?? '').replace(/[-\s]/g, ''), lookedUp: false });
    this.lookupError.set(null);
  }

  toggleManual(): void {
    this.patch({ manual: !this.manual() });
  }

  setField(subKey: string, raw: string, type: RdwFieldType): void {
    const value = type === 'number' ? (raw === '' ? null : Number(raw)) : raw;
    this.patch({ [subKey]: value });
    this.formControl.markAsDirty();
    this.formControl.markAsTouched();
  }

  clearField(subKey: string, type: RdwFieldType): void {
    this.setField(subKey, '', type);
  }

  /** Deviates: the field value is an object, so emptiness is the licence plate, not the value itself. */
  override hasClearableValue(): boolean {
    return this.plate() !== '';
  }

  hasManualValue(value: unknown): boolean {
    return value !== null && value !== undefined && value !== '';
  }

  /** Same trim + fallback semantics as the contract, but for a per-item label. */
  manualFieldClearAriaLabel(label: string): string {
    return buildClearAriaLabel(label);
  }

  /** Merge a partial into the object value and push it to the form control. */
  private patch(partial: Record<string, unknown>): void {
    const next = { ...this.obj(), ...partial };
    this.obj.set(next);
    this.formControl.setValue(next);
  }

  /** Coerce whatever the control holds (object, legacy plate string, or null) into the object shape. */
  private normalize(value: unknown): Record<string, unknown> {
    if (value && typeof value === 'object') {
      return value as Record<string, unknown>;
    }
    if (typeof value === 'string') {
      return { plate: value };
    }
    return {};
  }

  private runLookup(value: string): void {
    this.#lookupRestarted$.next();
    this.isLoading.set(true);
    this.invalidPlate.set(false);
    this.lookupError.set(null);

    // Defensive strip for values that entered the model with separators (e.g. legacy entries):
    // the backend reads the plate from the entry, so the stored value must be the bare form.
    value = (value ?? '').replace(/[-\s]/g, '');
    if (value !== this.plate()) {
      this.patch({ plate: value });
    }

    this.isDutchLicensePlate.set(this.validateDutchLicensePlate(value));
    if (!this.isDutchLicensePlate() || !value) {
      this.invalidPlate.set(true);
      this.isLoading.set(false);
      return;
    }

    this.apiMappingsStore
      .applyMapping({
        mappingId: this.field.props.apiMappingId,
        moduleId: this.options.formState.entity.id,
        entry: this.options.formState.entry,
        message: false,
      })
      .pipe(
        catchError(error => {
          console.error(error);
          this.isDutchLicensePlate.set(false);
          this.isLoading.set(false);
          this.lookupError.set(
            'Voertuiggegevens ophalen mislukt. Probeer het opnieuw of voer de gegevens handmatig in.'
          );
          return of();
        }),
        takeUntil(this.#lookupRestarted$),
        takeUntil(this.destroy$)
      )
      .subscribe(entry => {
        if (!entry) {
          return;
        }
        const result = (entry?.entry as Record<string, unknown>) ?? {};

        // The mapping writes its outputs to real entry keyPaths (merk/model/brandstof as their
        // own module fields). Those fields are not owned by this component, so patch the applied
        // entry into the root form — otherwise the lookup result is discarded. Same pattern as
        // the BAG verify in address.component.ts.
        UtilityService.getFormOfRootForm(this.field).formControl?.patchValue(result);

        const patch: Record<string, unknown> = {};
        for (const def of this.vehicleFieldDefs()) {
          // Only mapped fields land in the lookup result; unmapped fields stay manual-only.
          if (def.entryField) {
            const raw = this.getValueAtPath(result, def.entryField);
            // RDW returns dates as yyyymmdd; store ISO so the manual date input can render it.
            patch[def.subKey] = def.type === 'date' ? this.toIsoDate(raw) : raw;
          }
        }
        const categoryProfile = this.resolveCategoryProfile(result);
        patch[RDW_CATEGORY_PROFILE_KEY] = categoryProfile;
        this.patch({ ...patch, lookedUp: true });
        this.isLoading.set(false);
        this.fillSiblingsFromLookup();
      });
  }

  /**
   * Find the control to write an auto-fill result into.
   *
   * The service derives a control path by stripping `entry.general.` from the keyPath, which holds
   * for a flat entry form but not for a wizard, where steps and branches nest the controls deeper.
   * Look the field up by its keyPath in the live Formly tree instead — every field carries its own
   * control — and keep the derived path as a fallback.
   *
   * A repeated target is the exception: every row's field carries the same keyPath, so the walker
   * would hand back the first row. There the indexed control path is the only one that identifies
   * the row the result belongs to.
   */
  private resolveTargetControl(result: AutoFillResult): AbstractControl | null {
    if (result.repeatIndex?.length) {
      return this.formControl.root.get(result.formControlPath);
    }

    let root: FormlyFieldConfig = this.field;
    while (root.parent) {
      root = root.parent;
    }

    // Same canonical walker the rest of the portal uses to resolve a field by keyPath, but run over
    // the live tree instead of the generated config, so the field it returns carries its control.
    return (
      FormHelper.getFieldByKeyPath(result.targetFieldKeyPath, root.fieldGroup)?.formControl ??
      this.formControl.root.get(result.formControlPath)
    );
  }

  /**
   * Fill sibling fields from the completed lookup.
   *
   * Runs the field's `copyFieldsOnSelect` query-mappings, which look an entry up in another module
   * using the current entry as context — for assets that resolves the category carrying the profile
   * this lookup produced.
   *
   * Overwriting an existing value is the auto-fill service's own decision: it asks the user to
   * confirm before replacing something that is already filled in.
   */
  private fillSiblingsFromLookup(): void {
    const mappings = this.props['copyFieldsOnSelect'] as AutoFillMapping[] | undefined;
    const currentEntry = this.options.formState?.entry as Entry | undefined;
    const moduleId = this.options.formState?.entity?.id as string | undefined;
    const generatedForm = moduleId ? this.modulesStore.generatedForms()[moduleId] : null;

    if (!mappings?.length) {
      debugLog('RDW:autofill', 'geen copyFieldsOnSelect op dit veld', { keyPath: this.props['keyPath'] });
      return;
    }

    if (!currentEntry || !generatedForm) {
      debugLog('RDW:autofill', 'geen entry of generatedForm', {
        moduleId,
        heeftEntry: !!currentEntry,
        heeftGeneratedForm: !!generatedForm,
        bekendeModules: Object.keys(this.modulesStore.generatedForms() ?? {}).length,
      });
      return;
    }

    debugLog('RDW:autofill', 'mappings uitvoeren', {
      aantal: mappings.length,
      doelen: mappings.map(m => m.targetFieldKey),
    });

    // A query-mapping resolves its dynamic filter against formState.entry, and setting the form
    // control does not write through to it. Sync this field's own value first, or the profile
    // reads as empty and the mapping skips silently. Scoped to this field's own repeat position:
    // inside a fieldArray an unscoped write would stamp this plate onto every row.
    const ownIndices = extractArrayIndicesFromField(this.field);
    const keyPath = this.props['keyPath'] as string | undefined;
    if (keyPath && !setScopedKeyPath(currentEntry, keyPath, this.obj(), ownIndices)) {
      debugLog('RDW:autofill', 'eigen waarde niet in de entry te schrijven', { keyPath, ownIndices });
      return;
    }

    this.cascadingAutoFillService
      .processQueryAutoFill(mappings, currentEntry, generatedForm, this.field)
      .pipe(takeUntil(this.#lookupRestarted$), takeUntil(this.destroy$))
      .subscribe(results => {
        debugLog(
          'RDW:autofill',
          'resultaten',
          results.length,
          results.map(r => r.targetFieldKeyPath)
        );
        for (const result of results) {
          const control = this.resolveTargetControl(result);
          if (!control) {
            debugLog('RDW:autofill', 'doelveld niet gevonden', result.targetFieldKeyPath);
          } else {
            control.patchValue(result.value);
          }
          setScopedKeyPath(currentEntry, result.targetFieldKeyPath, result.value, result.repeatIndex ?? []);
        }
      });
  }

  /**
   * Derive the category profile from the lookup result.
   *
   * Read straight from the mapping result rather than from the configured vehicle fields: the
   * three RDW fields it needs (vehicle type, European category, body type) are plumbing for the
   * category match, not something a user should have to see in the vehicle-field list.
   *
   * Stored on the field's own value so a `copyFieldsOnSelect` query-mapping can match an
   * asset-category on it. Null when RDW gives no usable answer — the category then stays empty.
   */
  private resolveCategoryProfile(result: Record<string, unknown>): RdwCategoryProfile | null {
    const mapping = this.activeMapping();
    const read = (apiField: string): unknown => {
      const entryField = mapping?.outputFieldMappings?.find(o => o.apiField === apiField)?.entryField;
      return entryField ? this.getValueAtPath(result, entryField) : undefined;
    };

    return resolveRdwCategoryProfile({
      voertuigsoort: read(RDW_CATEGORY_API_FIELDS.vehicleType),
      europeseVoertuigcategorie: read(RDW_CATEGORY_API_FIELDS.europeanVehicleCategory),
      inrichting: read(RDW_CATEGORY_API_FIELDS.bodyType),
    });
  }

  validateDutchLicensePlate(value: string): boolean {
    if (!value) return false;

    const normalized = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();

    // license plate should be length of 6, 7 or 8
    if (![6, 7, 8].includes(normalized.length)) {
      return false;
    }

    // only numbers and letters are allowed
    if (!/^[A-Z0-9]+$/.test(normalized)) {
      return false;
    }

    // should be at least one letter and one number
    if (!/[A-Z]/.test(normalized) || !/[0-9]/.test(normalized)) {
      return false;
    }

    return true;
  }

  /** Normalize an RDW date (yyyymmdd, or an ISO-like string) to ISO `yyyy-MM-dd`. */
  private toIsoDate(value: unknown): unknown {
    if (typeof value !== 'string' && typeof value !== 'number') {
      return value;
    }
    const match = String(value)
      .trim()
      .match(/^(\d{4})-?(\d{2})-?(\d{2})/);
    return match ? `${match[1]}-${match[2]}-${match[3]}` : value;
  }

  /** Format a stored date (ISO or legacy yyyymmdd) as `dd-mm-yyyy` for the read-only list. */
  private formatDisplayDate(value: unknown): unknown {
    if (typeof value !== 'string' && typeof value !== 'number') {
      return value;
    }
    const match = String(value)
      .trim()
      .match(/^(\d{4})-?(\d{2})-?(\d{2})/);
    return match ? `${match[3]}-${match[2]}-${match[1]}` : value;
  }

  /**
   * Deep-reads a dot-separated keyPath (with an optional leading `entry.`, matching the
   * `outputFieldMappings[].entryField` format) from an apply-mapping result.
   */
  private getValueAtPath(source: Record<string, unknown>, keyPath: string): unknown {
    const segments = keyPath.replace(/^entry\./, '').split('.');
    return segments.reduce<unknown>((current, segment) => {
      if (current !== null && typeof current === 'object') {
        return (current as Record<string, unknown>)[segment];
      }
      return undefined;
    }, source);
  }
}
